import React, { FC } from 'react';
import styled from 'styled-components';

import { Box, Display, Flex, Heading, Image } from '@rover/kibble/core';
import Section from '@rover/kibble/patterns/Section';
import { MQ } from '@rover/kibble/styles';

import TrustPilot, { SizeOverride } from '../../../components/Trustpilot';
import defaultHeroImageDesktop from '../ServiceLanding/pages/shared-images/Hero_Generic_desktop.jpg';
import defaultHeroImageMobile from '../ServiceLanding/pages/shared-images/Hero_Generic_mobile.jpg';
import defaultHeroImageTablet from '../ServiceLanding/pages/shared-images/Hero_Generic_tablet.jpg';

import LandingSearchForm from './LandingSearchForm';

type BoxProps = React.ComponentProps<typeof Box>;
type DisplayProps = React.ComponentProps<typeof Display>;
type HeadingProps = React.ComponentProps<typeof Heading>;
type SearchFormProps = React.ComponentProps<typeof LandingSearchForm>;
type TrustPilotProps = React.ComponentProps<typeof TrustPilot>;

export type HeroImageSrcSet = {
  desktop: {
    src: string;
    height: number;
  };
  tablet: {
    src: string;
    height: number;
  };
  mobile: {
    src: string;
    height: number;
  };
};

export type LandingHeroProps = {
  heroImageSrcSet?: HeroImageSrcSet;
} & BoxProps;

type LandingHeroSubcomponents = {
  Content: FC<BoxProps>;
  Display: FC<DisplayProps>;
  Text: FC<HeadingProps>;
  SearchForm: FC<SearchFormProps>;
  TrustPilot: FC<TrustPilotProps>;
};

const DEFAULT_IMAGE_SET: HeroImageSrcSet = {
  desktop: {
    src: defaultHeroImageDesktop,
    height: 927,
  },
  tablet: {
    src: defaultHeroImageTablet,
    height: 927,
  },
  mobile: {
    src: defaultHeroImageMobile,
    height: 614,
  },
};

const StyledImage = styled(Image)`
  // crop from the left on small sizes
  object-position: 100% 0%;
  aspect-ratio: 1.25;

  ${MQ.SM_UP.toString()} {
    object-position: 50% 50%;
    aspect-ratio: auto;
  }
`;

const LandingHero: FC<LandingHeroProps> & LandingHeroSubcomponents = ({
  heroImageSrcSet = DEFAULT_IMAGE_SET,
  children,
  ...boxProps
}: LandingHeroProps) => {
  const { desktop, mobile, tablet } = heroImageSrcSet;

  return (
    <Box
      position="relative"
      height={['auto', '90vh']}
      maxHeight={['none', `${tablet.height}px`, `${desktop.height}px`]}
      {...boxProps}
    >
      <Section paddingTop="8x" position="relative" zIndex="1">
        {children}
      </Section>

      <Box
        position={['relative', 'absolute']}
        top="0"
        left="0"
        width="100%"
        height={[`auto`, '100%']}
      >
        <StyledImage
          src={[mobile.src, tablet.src, desktop.src]}
          description=""
          width="100%"
          height="100%"
          resizeMethod="cover"
        />
      </Box>
    </Box>
  );
};

LandingHero.Content = (props: BoxProps): JSX.Element => {
  return (
    <Box
      background="primary"
      borderRadius={['0px', 'primary']}
      pt={['0x', '8x']}
      pb="8x"
      px={['0x', '8x']}
      width={['100%', '480px']}
      {...props}
    >
      {props.children}
    </Box>
  );
};

LandingHero.Display = (props: DisplayProps): JSX.Element => {
  return (
    <Display
      data-qa-id="landing-hero-title"
      marginTop="4x"
      as="h1"
      size="500"
      responsive={false}
      {...props}
    >
      {props.children}
    </Display>
  );
};

LandingHero.Text = (props: HeadingProps): JSX.Element => {
  return (
    <Heading data-qa-id="landing-hero-subtitle" as="h2" fontWeight="regular" size="300" {...props}>
      {props.children}
    </Heading>
  );
};

LandingHero.SearchForm = (props: SearchFormProps): JSX.Element => {
  return <LandingSearchForm {...props} />;
};

LandingHero.TrustPilot = (props: TrustPilotProps): JSX.Element => {
  return (
    <Flex justifyContent={['center', 'left']}>
      <TrustPilot sizeOverride={SizeOverride.medium} paddingX="0x" {...props} />
    </Flex>
  );
};

export default LandingHero;
